<template>
  <!-- A bar the viewer may not move (`seeking=false`) is still drawn, as a progress bar: no
       focus, no pointer, no preview of a place it would not go. -->
  <div
    ref="containerRef"
    class="progress-container"
    :class="{ 'progress-container--still': !interactive }"
    :role="interactive ? 'slider' : 'progressbar'"
    :tabindex="interactive ? 0 : undefined"
    :aria-label="t('player.accessibility.progressBar')"
    :aria-valuenow="Math.floor(Math.max(0, currentTime - winStart))"
    aria-valuemin="0"
    :aria-valuemax="Math.floor(winLen)"
    :aria-valuetext="ariaTimeText"
    :aria-describedby="isHovering ? 'progress-tooltip' : undefined"
    @mousedown="onMouseDown"
    @mousemove="onMouseMove"
    @mouseleave="onMouseLeave"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
    @keydown="onKeydown"
  >
    <!-- Preview Tooltip -->
    <div
      v-if="isHovering"
      id="progress-tooltip"
      role="tooltip"
      class="preview-tooltip"
      :style="tooltipStyle"
    >
      <div class="preview-tooltip-compact">
        <div class="preview-tooltip-compact-time">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" style="margin-right: 4px;" aria-hidden="true">
            <path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zM8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM8 3.5a.5.5 0 0 1 .5.5v3.75l2.75 1.375a.5.5 0 1 1-.45.9l-3-1.5A.5.5 0 0 1 7.5 8V4a.5.5 0 0 1 .5-.5z"/>
          </svg>
          {{ hoverTimeFormatted }}
        </div>
        <div v-if="hoveredChapter" class="preview-tooltip-compact-divider"></div>
        <div v-if="hoveredChapter" class="preview-tooltip-compact-chapter">
          {{ hoveredChapter.title }}
        </div>
      </div>
    </div>

    <div class="progress-bar">
      <div
        v-if="bufferedAhead"
        :key="bufferedAhead.start"
        class="progress-buffered"
        :style="{
          left: pctOf(bufferedAhead.start) + '%',
          width: Math.max(0, pctOf(bufferedAhead.end) - pctOf(bufferedAhead.start)) + '%',
        }"
      />

      <div
        class="progress-played"
        :style="{ width: playedPercent + '%' }"
      />

      <div
        v-for="(chapter, index) in chapters.slice(1)"
        v-show="chapter.startTime >= winStart && chapter.startTime <= winEnd"
        :key="'chapter-' + index"
        class="chapter-marker"
        :style="{ left: pctOf(chapter.startTime) + '%' }"
      />

      <div
        v-for="marker in overlayMarkers"
        :key="'overlay-' + marker.uid"
        class="overlay-marker"
        :class="'overlay-marker--' + marker.type"
        :style="{ left: marker.percent + '%' }"
        aria-hidden="true"
      />

      <div
        v-if="isHovering"
        class="hover-indicator"
        :style="{ left: hoverPercent + '%' }"
      />

      <div
        class="progress-thumb"
        :style="{ left: playedPercent + '%' }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { formatTime } from '../../utils/time'
import { useVideoI18n } from '../../i18n'
import { bufferedAt, type TimeRange } from '../../../types/base'
import type { VideoChapter } from '../../../types/asset'
import type { OverlayConfig } from '../../../types/embed'

const props = withDefaults(defineProps<{
  currentTime: number
  duration: number
  /** Non-destructive trim window start (seconds). When set with trimEnd, the whole bar maps to [trimStart, trimEnd]. */
  trimStart?: number
  /** Non-destructive trim window end (seconds). */
  trimEnd?: number
  buffered: TimeRange[]
  chapters: VideoChapter[]
  overlays?: OverlayConfig[]
  /** Whether the viewer may move the playhead from the bar (`seeking`). */
  interactive?: boolean
}>(), {
  trimStart: undefined,
  trimEnd: undefined,
  overlays: undefined,
  interactive: true,
})

const emit = defineEmits<{
  seek: [time: number]
}>()

const { t } = useVideoI18n()

const containerRef = ref<HTMLDivElement | null>(null)
const isDragging = ref(false)
const isHovering = ref(false)
const hoverPercent = ref(0)
const hoveredChapter = ref<VideoChapter | null>(null)

// Non-destructive trim: when a window is set the whole bar represents
// [winStart, winEnd]; otherwise it spans the full media duration (unchanged behaviour).
const winStart = computed(() => Math.max(0, props.trimStart ?? 0))
const winEnd = computed(() => {
  const end = props.trimEnd ?? 0
  return end > winStart.value ? end : props.duration
})
const winLen = computed(() => Math.max(0, winEnd.value - winStart.value))

/** Map an absolute media time to a 0–100 position within the (possibly trimmed) window. */
function pctOf(time: number): number {
  if (winLen.value <= 0) return 0
  return Math.min(100, Math.max(0, ((time - winStart.value) / winLen.value) * 100))
}

/** Map a 0–100 bar position back to an absolute media time. */
function timeAtPercent(percent: number): number {
  return winStart.value + (percent / 100) * winLen.value
}

const playedPercent = computed(() => pctOf(props.currentTime))

/**
 * What is ready ahead of the playhead — see [`bufferedAt`] for why it is one range and not all
 * of them.
 *
 * It is keyed on `start` in the template rather than by position, and that is load-bearing with
 * the 0.3s transition below: while a range grows forward its start holds, so the same element
 * keeps animating its width; a seek lands in a different range, whose different start makes a
 * *new* element appear where it belongs instead of the old one sliding across the bar.
 */
const bufferedAhead = computed<TimeRange | undefined>(() =>
  bufferedAt(props.buffered, props.currentTime),
)

const hoverTimeFormatted = computed(() => formatTime(timeAtPercent(hoverPercent.value)))

const ariaTimeText = computed(() =>
  t('player.accessibility.timeOf', {
    current: formatTime(Math.max(0, props.currentTime - winStart.value)),
    total: formatTime(winLen.value),
  })
)

const tooltipStyle = computed(() => {
  if (!containerRef.value) return {}
  const containerRect = containerRef.value.getBoundingClientRect()
  const mouseX = (hoverPercent.value / 100) * containerRect.width

  const estimatedWidth = hoveredChapter.value ? 200 : 80
  const halfWidth = estimatedWidth / 2
  const margin = 10

  let left = mouseX - halfWidth

  if (left < margin) {
    left = margin
  } else if (left + estimatedWidth > containerRect.width - margin) {
    left = containerRect.width - estimatedWidth - margin
  }

  return {
    left: `${left}px`,
    maxWidth: '250px',
  }
})

interface OverlayMarker {
  uid: string
  time: number
  percent: number
  type: string
}

const overlayMarkers = computed<OverlayMarker[]>(() => {
  if (!props.overlays?.length || props.duration <= 0) return []
  return props.overlays
    .filter((o) =>
      o.showMarker !== false && (
        o.triggerType === 'time' ||
        o.triggerType === 'percent' ||
        o.triggerType === 'video_start' ||
        o.triggerType === 'video_end'
      )
    )
    .map((o) => {
      let time: number
      switch (o.triggerType) {
        case 'time':
          time = o.triggerTime ?? 0
          break
        case 'percent':
          time = ((o.triggerPercent ?? 0) / 100) * props.duration
          break
        case 'video_end':
          time = winEnd.value
          break
        default: // video_start
          time = winStart.value
      }
      return { uid: o.uid, time, percent: pctOf(time), type: o.overlayType }
    })
    // Hide markers that fall outside the trimmed window.
    .filter((m) => m.time >= winStart.value - 0.001 && m.time <= winEnd.value + 0.001)
})

function findChapterAtTime(time: number): VideoChapter | null {
  if (!props.chapters || props.chapters.length === 0) return null
  for (let i = props.chapters.length - 1; i >= 0; i--) {
    if (time >= props.chapters[i].startTime) {
      return props.chapters[i]
    }
  }
  return null
}

function getPercentFromEvent(event: MouseEvent | Touch): number {
  if (!containerRef.value) return 0
  const rect = containerRef.value.getBoundingClientRect()
  const x = event.clientX - rect.left
  return Math.min(100, Math.max(0, (x / rect.width) * 100))
}

function seekToPercent(percent: number): void {
  emit('seek', timeAtPercent(percent))
}

function onMouseDown(event: MouseEvent): void {
  if (!props.interactive) return
  event.preventDefault()
  isDragging.value = true
  const percent = getPercentFromEvent(event)
  seekToPercent(percent)

  const onMouseMoveGlobal = (e: MouseEvent) => {
    e.preventDefault()
    const p = getPercentFromEvent(e)
    seekToPercent(p)
  }

  const onMouseUp = () => {
    isDragging.value = false
    isHovering.value = false
    window.removeEventListener('mousemove', onMouseMoveGlobal)
    window.removeEventListener('mouseup', onMouseUp)
  }

  window.addEventListener('mousemove', onMouseMoveGlobal)
  window.addEventListener('mouseup', onMouseUp)
}

function onMouseMove(event: MouseEvent): void {
  if (!props.interactive) return
  isHovering.value = true
  hoverPercent.value = getPercentFromEvent(event)
  const hoverTime = timeAtPercent(hoverPercent.value)
  hoveredChapter.value = findChapterAtTime(hoverTime)
}

function onMouseLeave(): void {
  if (!isDragging.value) {
    isHovering.value = false
    hoveredChapter.value = null
  }
}

function onTouchStart(event: TouchEvent): void {
  if (!props.interactive) return
  event.preventDefault()
  if (event.touches.length > 0) {
    isDragging.value = true
    const percent = getPercentFromEvent(event.touches[0])
    seekToPercent(percent)
  }
}

function onTouchMove(event: TouchEvent): void {
  if (!props.interactive) return
  event.preventDefault()
  if (event.touches.length > 0) {
    const percent = getPercentFromEvent(event.touches[0])
    seekToPercent(percent)
  }
}

function onTouchEnd(): void {
  isDragging.value = false
}

function onKeydown(event: KeyboardEvent): void {
  if (!props.interactive) return
  const step = 5
  switch (event.key) {
    case 'ArrowRight':
      event.preventDefault()
      emit('seek', Math.min(props.duration, props.currentTime + step))
      break
    case 'ArrowLeft':
      event.preventDefault()
      emit('seek', Math.max(0, props.currentTime - step))
      break
    case 'Home':
      event.preventDefault()
      emit('seek', 0)
      break
    case 'End':
      event.preventDefault()
      emit('seek', props.duration)
      break
  }
}
</script>

<style scoped>
.progress-container {
  position: relative;
  width: 100%;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  -webkit-user-select: none;
}

.progress-container--still {
  cursor: default;
  touch-action: auto;
}

.progress-container:focus-visible {
  outline: 2px solid var(--vp-primary-color, #007bff);
  outline-offset: 2px;
}

.progress-bar {
  position: relative;
  width: 100%;
  height: 10px;
  border-radius: 5px;
  background: var(--vp-progress-bg, rgba(255, 255, 255, 0.2));
  overflow: visible;
}

.progress-played {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 5px;
  /* A preset's `progress_bar_color` when it sets one; the player's accent otherwise, which is
     what this bar has always fallen back to. */
  background: var(--vp-progress-played-color, var(--vp-primary-color, #007bff));
  z-index: 3;
  pointer-events: none;
}

.progress-buffered {
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 5px;
  background: var(--vp-progress-buffered-color, rgba(255, 255, 255, 0.4));
  z-index: 2;
  pointer-events: none;
  transition: left 0.3s ease, width 0.3s ease;
}

.progress-thumb {
  position: absolute;
  top: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: var(--vp-progress-thumb-color, #ffffff);
  transform: translate(-50%, -50%);
  z-index: 12;
  transition: width 0.15s ease, height 0.15s ease;
  pointer-events: none;
}

.progress-container:not(.progress-container--still):hover .progress-thumb {
  width: 18px;
  height: 18px;
}

/* Chapter Markers */
.chapter-marker {
  position: absolute;
  top: 0;
  width: 3px;
  height: 100%;
  background: var(--vp-progress-buffered-color, rgba(255, 255, 255, 0.4));
  transform: translateX(-50%);
  z-index: 4;
  pointer-events: none;
  /* Transform only: a width, a shadow or a colour in a transition is laid out or painted again on
     every frame of it, over a playing video. The hover's colour change is immediate. */
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
}

.progress-container:hover .chapter-marker {
  /* 3px drawn 5px wide. */
  transform: translateX(-50%) scale(1.667, 1.4);
  background: var(--vp-progress-thumb-color, #ffffff);
}

/* Overlay Markers — always over video so keep white-on-dark */
.overlay-marker {
  position: absolute;
  top: 50%;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.9);
  transform: translate(-50%, -50%);
  z-index: 6;
  pointer-events: none;
  background: rgba(255, 255, 255, 0.85);
  /* The dark edge keeps the marker readable on a bright picture; no glow, and nothing but the
     transform in the transition. */
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
  transition: transform 200ms ease;
}

.overlay-marker--cta {
  background: #60a5fa;
  border-color: #fff;
}

.overlay-marker--rating {
  background: #fbbf24;
  border-color: #fff;
}

.progress-container:hover .overlay-marker {
  transform: translate(-50%, -50%) scale(1.3);
}

/* Hover Indicator */
.hover-indicator {
  position: absolute;
  top: 0;
  height: 100%;
  width: 2px;
  background: var(--vp-progress-thumb-color, #ffffff);
  opacity: 0.7;
  transform: translateX(-50%);
  z-index: 5;
  pointer-events: none;
}

/* Preview Tooltip — always sits over video, keeps dark background */
.preview-tooltip {
  position: absolute;
  bottom: calc(100% + 20px);
  background: var(--vp-tooltip-bg, rgba(0, 0, 0, 0.95));
  color: var(--vp-tooltip-color, #ffffff);
  border-radius: 6px;
  pointer-events: none;
  z-index: 15;
  width: auto;
  max-width: 300px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  backdrop-filter: blur(8px);
  border: 1px solid var(--vp-border-default, rgba(255, 255, 255, 0.1));
  transition: left 0.1s ease-out;
}

.preview-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 8px solid transparent;
  border-top-color: var(--vp-tooltip-bg, rgba(0, 0, 0, 0.95));
}

.preview-tooltip-compact {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  gap: 12px;
  white-space: nowrap;
}

.preview-tooltip-compact-time {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  font-family: monospace;
  color: var(--vp-tooltip-color, #ffffff);
  opacity: 0.95;
  white-space: nowrap;
}

.preview-tooltip-compact-divider {
  width: 1px;
  height: 16px;
  background: var(--vp-tooltip-color, #ffffff);
  opacity: 0.2;
  flex-shrink: 0;
}

.preview-tooltip-compact-chapter {
  font-size: 13px;
  color: var(--vp-tooltip-color, #ffffff);
  opacity: 0.8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

/* Touch support: increased hit area */
@media (pointer: coarse) {
  .progress-container {
    height: 40px;
  }

  .progress-container:not(.progress-container--still):hover .progress-thumb,
  .progress-container:not(.progress-container--still) .progress-thumb {
    width: 18px;
    height: 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .progress-buffered {
    transition: none;
  }

  .progress-thumb {
    transition: none;
  }

  .chapter-marker {
    transition: none;
  }

  .overlay-marker {
    transition: none;
  }
}
</style>
